<template>

  <v-container class="pa-0 ma-0 step delivery-step-head-to-shipper" v-if="route && orders && shipperLocation">

    <!-- Shipper info -->
    <v-row class="pa-0 ma-0">
      <v-col sm="12" class="ma-0 pa-0 content-accent">{{ shipperLocation.name }}</v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="ma-0 pa-0 pt-2 address-text d-flex align-center">
        <div>
          <div>{{ formatAddress(shipperLocation).street_1 }}</div>
          <div v-if="formatAddress(shipperLocation).street_2">{{ formatAddress(shipperLocation).street_2 }}</div>
          <div v-if="formatAddress(shipperLocation).area">{{ formatAddress(shipperLocation).area }}</div>
        </div>
        <v-btn plain color="primary" class="ml-6" @click="copyAddress">
          <v-icon class="icon-copy">mdi-content-copy</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Heading to shipper button -->
    <v-row class="pa-0 ma-0 mt-2">
      <v-col sm="12" class="pa-0 ma-0 mt-4 d-flex flex-column align-left justify-center">
        <v-btn color="primary" v-if="isToShipperProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedHeadingToShipper">
          En route at {{ formatDateTime(hasCompletedHeadingToShipper) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Heading to shipper"
            confirmTitle="Ready to head to the shipper?"
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="isHeadingToShipper || isAtShipper"
            @confirmed="headToShipper" />
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col sm="12" class="ma-0 pa-0 mt-2 mr-8 instructions-text" v-if="showDrivingDirections">
        (click to get directions)
      </v-col>
    </v-row>

    <!-- Directions link -->
    <v-row class="pa-0 ma-0 mt-3">
      <v-col sm="12" class="ma-0 pa-0">
        <v-btn text color="primary" :disabled="!isHeadingToShipper" v-if="showDrivingDirections">
          <v-icon class="icon-open-in-new">mdi-open-in-new</v-icon>
          <span>Driving directions</span>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Order info -->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col sm="12" class="ma-0 pa-0 content-title">
        <span v-if="orders.length > 1">Pick-up orders:</span>
        <span v-else>Pick-up order:</span>
        <span style="font-size:1.2rem;" class="ml-1 content-accent">{{ orderNumbersForDisplay }}</span>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-3">
      <v-col sm="12" class="ma-0 pa-0 content-title">
        From: <span class="content-accent">{{ shipperLocation.name }}</span>
      </v-col>
    </v-row>

    <!-- Complete step -->
    <v-row class="mt-6 mx-0 mb-4 pa-0 complete-step">
      <v-col cols="12" class="d-flex flex-column justify-center pa-0 ma-0">
        <v-btn color="primary" v-if="isAtShipperProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedArrivedAtShipper">
          Arrived at {{ formatDateTime(hasCompletedArrivedAtShipper) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Arrived"
            confirmTitle="Arrived at the shipper?"
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="!isHeadingToShipper"
            @confirmed="completeStep" />
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'

export default {
  name: 'DeliveryStepHeadToShipper',

  components: {
    DoubleClickConfirmButton
  },

  props: {
    route: {
      required: true
    },
    orders: {
      type: Array,
      required: true
    },
    stepNumber: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      showDrivingDirections: false,
      isToShipperProcessing: false,
      isAtShipperProcessing: false
    }
  },

  computed: {
    isTestRoute () {
      return this.route.is_test
    },
    firstOrder () {
      return (this.orders.length > 0) ? this.orders[0] : {}
    },
    orderNumbersForDisplay () {
      return (this.orders.length > 0)
        ? this.orders.map(order => ` ${order.thirdparty_order_number}`).toString()
        : ''
    },
    shipperLocation () {
      return this.firstOrder.shipper
    },
    isHeadingToShipper () {
      return this.firstOrder.order_status === 'TO_SHIPPER'
    },
    isAtShipper () {
      return this.firstOrder.order_status === 'AT_SHIPPER'
    },
    hasCompletedHeadingToShipper () {
      return this.firstOrder.to_shipper_at
    },
    hasCompletedArrivedAtShipper () {
      return this.firstOrder.at_shipper_at
    }
  },

  methods: {
    // copy address to clipboard
    copyAddress () {
      this.$root.copyToClipboard(this.$root.utils.getAddressTextFromData(this.shipperLocation))
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D h:mma') : ''
    },

    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data, { multi_lines: true })
    },

    // Set orders: to shipper
    async headToShipper () {
      this.isToShipperProcessing = true
      const orderIds = this.orders.map(it => it.id)

      try {
        const result = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'orders/to-shipper',
          opts: {
            bodyData: { order_ids: orderIds }
          },
          to_model: false
        })
        const updatedOrders = result.updated || []
        // Sync into data registries
        for (const orderData of updatedOrders) {
          const order = this.$root.toModel(orderData)
          this.$store.dispatch('sh/syncData', { resource: 'activeDelivery', nested: 'orders', data: order })
          this.$store.dispatch('sh/syncItemEditing', { resource: 'activeDelivery', nested: 'orders', data: order })
        }
      } catch (err) {
        console.error('Error setting orders: to-shipper', err)
      } finally {
        this.isToShipperProcessing = false
      }
    },

    // Set orders: at shipper and complete step
    async completeStep () {
      this.isAtShipperProcessing = true
      const orderIds = this.orders.map(it => it.id)

      try {
        const result = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'orders/at-shipper',
          opts: {
            bodyData: { order_ids: orderIds }
          },
          to_model: false
        })
        const updatedOrders = result.updated || []
        // Sync into data registries
        for (const orderData of updatedOrders) {
          const order = this.$root.toModel(orderData)
          this.$store.dispatch('sh/syncData', { resource: 'activeDelivery', nested: 'orders', data: order })
          this.$store.dispatch('sh/syncItemEditing', { resource: 'activeDelivery', nested: 'orders', data: order })
        }

        // Emit step completed
        this.$emit('completed')
      } catch (err) {
        console.error('Error setting orders: at-shipper', err)
      } finally {
        this.isAtShipperProcessing = false
      }
    }
  }
}
</script>

<style scoped>
</style>
